import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Search, X, Clock, Loader2 } from 'lucide-react';
import { components, palette } from '../utils/designTokens';

const PLACEHOLDER = 'Search traces and sessions';
// All dimensions come from the searchField design tokens (single source).
// Resolved INSIDE the component (not at module scope): a bad token path then
// surfaces as a render error the ErrorBoundary can catch, instead of a
// bundle-evaluation crash that blanks the whole app.

/**
 * ExploreSearchField — the Explore tab's query surface.
 *
 * One bordered container holding BOTH the search input and the filter strip
 * (passed as filterSlot, tucked directly under the text, no divider): search text + filter chips
 * read as one query, ANDed — and chips alone (empty input) read as filtering
 * without search. The same strip is where Tier 2's interpretation chips will
 * render. Width-capped so it reads as a query surface, not a filter strip.
 * Autogrows from its collapsed single-line height to three lines, then scrolls. Enter submits,
 * Shift+Enter inserts a newline; a labeled Search button on the right edge is
 * the pointer affordance and doubles as the progress indicator ("Searching" +
 * spinner while loading). A static, muted placeholder avoids reading as input;
 * recent searches live in a dropdown shown on focus.
 */
export default function ExploreSearchField({
  value,
  onChange,
  onSubmit,
  onClear,
  active = false,
  loading = false,
  recents = [],
  onPickRecent,
  filterSlot = null,
}) {
  const T = components.searchField;
  const COLLAPSED = T.collapsedHeight;
  const LINE_H = T.lineHeight;
  const V_PAD = COLLAPSED - LINE_H;
  const MAX_H = LINE_H * T.maxLines + V_PAD;

  const ref = useRef(null);
  const blurTimerRef = useRef(null);
  const [focused, setFocused] = useState(false);

  useEffect(() => () => clearTimeout(blurTimerRef.current), []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_H)}px`;
  }, [value]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (value.trim()) onSubmit();
    }
  }, [value, onSubmit]);

  const hasText = !!value.trim();
  const showDropdown = focused && !value && recents.length > 0;

  const renderPick = (text, Icon, key) => (
    <button
      key={key}
      type="button"
      // onMouseDown (not onClick) so it fires before the textarea blur closes the dropdown.
      onMouseDown={(e) => { e.preventDefault(); onPickRecent(text); }}
      className="flex items-center gap-2 w-full text-left hover:bg-background-tertiary"
      style={{ padding: '8px 12px', fontSize: T.dropdownFontSize, color: palette.text.primary }}
    >
      <Icon size={13} style={{ color: palette.text.muted, flexShrink: 0 }} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</span>
    </button>
  );

  const sectionLabel = (t) => (
    <div style={{ padding: '6px 12px', fontSize: T.sectionLabelSize, textTransform: 'uppercase', letterSpacing: 0.4, color: palette.text.muted }}>
      {t}
    </div>
  );

  return (
    <div className="relative" style={{ flex: '1 1 auto', maxWidth: 760, minWidth: 260 }}>
      <div
        className={`relative rounded-lg bg-background-secondary border ${active ? 'border-accent-primary-base' : 'border-border-subtle'}`}
      >
        <div className="relative flex items-start" style={{ minHeight: COLLAPSED }}>
          <Search size={17} style={{ position: 'absolute', left: 14, top: 19, color: palette.text.muted, pointerEvents: 'none' }} />

          <textarea
            ref={ref}
            rows={1}
            value={value}
            aria-label={PLACEHOLDER}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={() => { blurTimerRef.current = setTimeout(() => setFocused(false), 120); }}
            style={{
              width: '100%',
              resize: 'none',
              paddingLeft: 40,
              paddingRight: active ? 148 : 120,
              paddingTop: V_PAD / 2,
              paddingBottom: V_PAD / 2,
              lineHeight: `${LINE_H}px`,
              height: COLLAPSED,
              maxHeight: MAX_H,
              border: 'none',
              backgroundColor: 'transparent',
              color: palette.text.primary,
              outline: 'none',
              fontSize: T.fontSize,
            }}
          />

          {/* Static muted placeholder — overlay so its color is guaranteed muted, not input-bright. */}
          {!value && (
            <span style={{ position: 'absolute', left: 40, top: (COLLAPSED - LINE_H) / 2, color: palette.text.muted, fontSize: T.fontSize, pointerEvents: 'none' }}>
              {PLACEHOLDER}
            </span>
          )}

          {active && !loading && (
            <button
              type="button"
              onClick={onClear}
              aria-label="Clear search"
              style={{ position: 'absolute', right: 118, top: 16, color: palette.text.muted, lineHeight: 1 }}
            >
              <X size={15} />
            </button>
          )}

          {/* Search action — labeled button; doubles as the progress indicator. */}
          <button
            type="button"
            onClick={() => (hasText ? onSubmit() : ref.current?.focus())}
            disabled={loading}
            title="Search (Enter)"
            className={`inline-flex items-center justify-center gap-1.5 text-sm font-medium rounded-md transition-colors ${
              loading
                ? 'text-text-supporting cursor-default'
                : hasText
                  ? 'bg-accent-primary-base text-text-inverse'
                  : 'bg-background-tertiary text-text-muted'
            }`}
            style={{ position: 'absolute', right: 10, top: (COLLAPSED - T.buttonHeight) / 2, height: T.buttonHeight, padding: '0 16px' }}
          >
            {loading ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Searching
              </>
            ) : (
              'Search'
            )}
          </button>
        </div>

        {/* Filter strip — filters are part of the same query surface: chips AND
            with the search text, and work standalone when the input is empty.
            This is also where Tier 2's interpretation chips will render. */}
        {filterSlot && (
          <div
            className="flex items-center flex-wrap gap-2"
            style={{ padding: '0 12px 10px 40px', marginTop: -4 }}
          >
            {filterSlot}
          </div>
        )}
      </div>

      {showDropdown && (
        <div
          className="absolute left-0 right-0 z-30 bg-background-primary border border-border-subtle rounded-lg shadow-lg overflow-hidden"
          style={{ top: 'calc(100% + 4px)', paddingBottom: 4 }}
        >
          {sectionLabel('Recent')}
          {recents.map((r) => renderPick(r, Clock, `recent-${r}`))}
        </div>
      )}
    </div>
  );
}
